import {
  mapImage,
  mapLink,
  mapOverlayDirection,
  mapOverlayOpacity,
  mapText,
  mapTitle,
  normalizeSpacer,
  pickStr,
} from "@/components/DesignSystem/utils/teaserHelper"
import { handleCTAForFeatureCards } from "@/components/DesignSystem/analytics/twoUpfeatureCardsAnalytics"
import { validateTwoUpFeaturedCardsProps } from "../validators/twoUpFeaturedCardsValidator"
import { modifyHref } from "@/components/Shared/HeaderNavTabs/utils"

/**
 * Maps a “Two-Up Featured Cards” AEM container into KDS props.
 *
 * Picks first child by `:itemsOrder` (fallback: first key in `:items`).
 * Produces:
 * - `mod` (defaults to "white")
 * - `variant` only when ∈ {"default","spacious"}
 * - `topSpacer` / `bottomSpacer` via `normalizeSpacer`
 * - `cards[]` (paired by index with `media` when present). Each card requires:
 *    - `image` from `media[index].image` OR `card.image`
 *    - `title` via `mapTitle(card.title)`
 *    - `CTA` from the first `card.cta` entry
 *    - Optional: `opacity`/`direction` (overlay), `showCTA` boolean
 *
 * Returns `null` when no child node is found. Omits empty keys.
 *
 * @param {Object} container AEM container node with `:itemsOrder` and `:items`.
 * @return {Object|null} Mapped props or `null`.
 */
export function mapTwoUpFeaturedCardsProps(container) {
  try {
    const firstKey =
      container?.[":itemsOrder"]?.[0] ??
      Object.keys(container?.[":items"] ?? {})[0]

    const node = firstKey ? container?.[":items"]?.[firstKey] : undefined
    if (!node) return null

    const basic = node.basic
    const cardsRaw = node.card?.cards
    const mod = basic?.mod || "white"
    const variant = (() => {
      const raw = pickStr(basic?.variant)
      const ok = new Set(["default", "spacious"])
      return raw && ok.has(raw) ? raw : undefined
    })()
    const topSpacer = basic?.topSpacer
      ? normalizeSpacer(basic.topSpacer.size ?? node.topSpacer)
      : undefined
    const bottomSpacer = basic?.bottomSpacer
      ? normalizeSpacer(basic.bottomSpacer.size ?? basic.bottomSpacer)
      : undefined

    if (cardsRaw?.length === 0) return null

    const cards = cardsRaw
      ?.filter(c => !c.CTA)
      ?.map(c => {
        const image = mapImage(c?.image?.[0])
        const CTA = mapLink(c?.cta?.[0])
        if (CTA) {
          CTA.onClick = () => handleCTAForFeatureCards(CTA.text)
          CTA.href = modifyHref(CTA.href)
          CTA.target = CTA?.isNewTab ? "_blank" : "_self"
        }

        const title = mapTitle(c?.title?.[0])
        const opacity = mapOverlayOpacity(c?.opacity)
        const direction = mapOverlayDirection(c?.gradientDirection)
        const eyebrow = mapText(c?.eyebrow?.[0])
        return image && title
          ? {
              image,
              showCTA: Boolean(CTA),
              title,
              ...(eyebrow ? { eyebrow } : {}),
              ...(CTA ? { CTA } : {}),
              ...(opacity ? { opacity } : {}),
              ...(direction ? { direction } : {}),
            }
          : undefined
      })
      ?.filter(Boolean)
    if (!cards || cards.length === 0) {
      return null
    }

    const twoUpFeaturedCardsProp = {
      ...(mod ? { mod } : {}),
      ...(variant ? { variant } : {}),
      ...(cards.length ? { cards } : {}),
      ...(topSpacer ? { topSpacer } : {}),
      ...(bottomSpacer ? { bottomSpacer } : {}),
    }

    const { isValid, errors } = validateTwoUpFeaturedCardsProps(
      twoUpFeaturedCardsProp
    )

    if (!isValid) {
      console.warn("twoUpFeaturedCardsProp validation errors:", errors)
      return null
    }

    return twoUpFeaturedCardsProp
  } catch (error) {
    console.error("Error in mapTwoUpFeaturedCardsProps:", error)
    return null
  }
}
